import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

export type NewCodingEntry = { ID: string; path: string };
export type CodingEntryTeam = {
	teamId: string;
	teamName: string;
	state: boolean;
};

type CodingEntryState = {
	codingEntry: {
		// for ui as the changes can be cancelled
		currentSelectedCodingEntry: NewCodingEntry;
		currentCodingEntryTeams: CodingEntryTeam[];
		// for api calls
		newCodingEntry: NewCodingEntry;
		newCodingEntryTeams: CodingEntryTeam[];
	};
};

type CodingEntryActions = {
	setCurrentSelectedCodingEntry: (
		currentSelectedCodingEntry: CodingEntryState['codingEntry']['currentSelectedCodingEntry'],
	) => void;
	setCurrentSelectedCodingEntryTeams: (
		currentSelectedCodingEntry: CodingEntryState['codingEntry']['currentCodingEntryTeams'],
	) => void;
	setNewCodingEntry: (newCodingEntry: CodingEntryState['codingEntry']['newCodingEntry']) => void;
	setNewCodingEntryTeams: (newCodingEntryTeams: CodingEntryState['codingEntry']['newCodingEntryTeams']) => void;
	updateSelectedCodingEntryTeamByTeamId: (teamToUpdate: CodingEntryTeam) => void;
};

export type CodingEntrySlice = CodingEntryState & CodingEntryActions;

const codingEntryInitialState: CodingEntryState = {
	codingEntry: {
		currentSelectedCodingEntry: { ID: '', path: '' },
		currentCodingEntryTeams: [],
		newCodingEntry: { ID: '', path: '' },
		newCodingEntryTeams: [],
	},
};

export function codingEntrySlice(set: NamedSet<ZustandStore>): CodingEntrySlice {
	return {
		...codingEntryInitialState,
		setCurrentSelectedCodingEntry(
			currentSelectedCodingEntry: CodingEntryState['codingEntry']['currentSelectedCodingEntry'],
		): void {
			set(
				(state: CodingEntryState) => {
					state.codingEntry.currentSelectedCodingEntry.ID = currentSelectedCodingEntry.ID || '';
					state.codingEntry.currentSelectedCodingEntry.path = currentSelectedCodingEntry.path || '';
					return state;
				},
				false,
				'[setCurrentSelectedCodingEntry]',
			);
		},

		setCurrentSelectedCodingEntryTeams(
			currentSelectedCodingEntryTeams: CodingEntryState['codingEntry']['currentCodingEntryTeams'],
		): void {
			set(
				(state: CodingEntryState) => {
					state.codingEntry.currentCodingEntryTeams = currentSelectedCodingEntryTeams;
					return state;
				},
				false,
				'[setCurrentSelectedCodingEntryTeams]',
			);
		},

		updateSelectedCodingEntryTeamByTeamId(teamToUpdate: CodingEntryTeam): void {
			set(
				(state: CodingEntryState) => {
					const teamIndex = state.codingEntry.currentCodingEntryTeams.findIndex(
						team => team.teamId === teamToUpdate.teamId,
					);
					if (teamIndex !== -1) {
						state.codingEntry.currentCodingEntryTeams[teamIndex] = teamToUpdate;
					}
					return state;
				},
				false,
				'[updateSelectedCodingEntryTeamByTeamId]',
			);
		},

		setNewCodingEntry(newCodingEntry: CodingEntryState['codingEntry']['newCodingEntry']): void {
			set(
				(state: CodingEntryState) => {
					state.codingEntry.newCodingEntry.ID = newCodingEntry.ID || '';
					state.codingEntry.newCodingEntry.path = newCodingEntry.path || '';
					return state;
				},
				false,
				'[setNewCodingEntry]',
			);
		},

		setNewCodingEntryTeams(newCodingEntryTeams: CodingEntryState['codingEntry']['newCodingEntryTeams']): void {
			set(
				(state: CodingEntryState) => {
					state.codingEntry.newCodingEntryTeams = newCodingEntryTeams;
					return state;
				},
				false,
				'[setNewCodingEntryTeams]',
			);
		},
	};
}
